import type { SalesMetricType } from '@api';
import { formatDate, formatNumber } from '@common';
import { Flex, Stack, Text, useMantineTheme } from '@mantine/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { useTranslation } from 'react-i18next';
import { TimelineComparisonSummary } from './components';
import {
  OnClickEventType,
  useSalesTimelineChartOption,
  useTimelineComparison,
} from './hooks';
import { echarts } from './ticketSalesECharts';

const TRANS_PATH = 'TABLE.SALES_TIMELINE.TOOLTIP';
const CHART_WIDTH_PER_DATAPOINT = 15;
const CHART_HEIGHT = 60;

type TicketSalesTimelineCellPropsType = {
  salesTimeline: SalesMetricType[];
  noDataPlaceholder: string;
};

export const TicketSalesTimelineCell: React.FC<
  TicketSalesTimelineCellPropsType
> = ({ salesTimeline, noDataPlaceholder }) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(['ticketSales', 'common']);
  const {
    clearComparison,
    handleClick,
    increaseBetweenSelectedData,
    selectedDate1,
    selectedDate2,
  } = useTimelineComparison({ salesTimeline });
  const lineColor = theme.colors.blue[6];
  const barColor = theme.colors.blue[2];
  const selectedLineColor = theme.colors.orange[6];
  const selectedBarColor = theme.colors.orange[3];

  const option = useSalesTimelineChartOption({
    salesTimeline,
    themeColors: {
      lineColor,
      barColor,
      selectedLineColor,
      selectedBarColor,
    },
    selectedDate1,
    selectedDate2,
  });

  if (salesTimeline.length === 0) {
    return noDataPlaceholder;
  }

  const isWaitingForSecondClick = selectedDate1 && !selectedDate2;

  return (
    <Stack>
      <Flex justify="flex-end" w="100%">
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          onEvents={{
            click: (event: OnClickEventType) => handleClick(event),
          }}
          notMerge
          style={{
            width: salesTimeline.length * CHART_WIDTH_PER_DATAPOINT,
            height: CHART_HEIGHT,
          }}
        />
      </Flex>
      {isWaitingForSecondClick && (
        <Text size="xs" c="dimmed" ta="right">
          {t(`${TRANS_PATH}.WAITING_FOR_SECOND_CLICK`)}
        </Text>
      )}
      {increaseBetweenSelectedData !== null && (
        <TimelineComparisonSummary
          formattedDateRange={`${formatDate(selectedDate1 ?? '')} – ${formatDate(selectedDate2 ?? '')}`}
          formattedIncrease={
            increaseBetweenSelectedData > 0
              ? `+${formatNumber(increaseBetweenSelectedData)}`
              : formatNumber(increaseBetweenSelectedData)
          }
          onClear={clearComparison}
          clearAriaLabel={t('QUERY_BUILDER.CLEAR', { ns: 'common' })}
        />
      )}
    </Stack>
  );
};
